import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api.js';
import { money } from '../../utils/money.js';

function statusBadge(s) {
  switch (s) {
    case 'Success':
      return 'bg-success';
    case 'Failed':
      return 'bg-danger';
    case 'Processing':
    case 'Retrying':
      return 'bg-info text-dark';
    default:
      return 'bg-secondary';
  }
}

const when = (ts) => (ts ? new Date(ts).toLocaleString() : null);
const amount = (n, ccy) => (n == null ? null : money(n, ccy));

/** The billing inputs handed to SAP, as label / value pairs. */
/** 8956: what SAP got for a booking's consolidated invoice — the header rows. */
function invoiceRows(i) {
  return [
    ['Sales invoice', i.invoiceNo],
    ['SAP customer', i.sapCustomerId],
    ['Booking', i.bookingRef],
    ['Vessel', i.vesselName],
    ['Service', i.serviceName],
    ['Port / area', [i.portName, i.assistanceAreaName].filter(Boolean).join(' — ') || null],
    ['Pricing', i.rateBasis],
    ['Jobs', i.lines.length],
    ['Base charge', amount(i.baseCharge, i.currency)],
    ['Standby fees', amount(i.standbyFees, i.currency)],
    ['Surcharges', amount(i.surchargeAmount, i.currency)],
    ['VAT', amount(i.vatAmount, i.currency)],
    ['Total', amount(i.netAmount, i.currency)],
    ["Confirmed by (ship's master)", i.assistConfirmedBy],
  ];
}

function inputRows(i) {
  if (Array.isArray(i.lines)) return invoiceRows(i);
  return [
    ['Dispatch ticket', i.ticketNo],
    ['SAP customer', i.sapCustomerId],
    ['Vessel', i.vesselName],
    ['Tug', i.tugName],
    ['Port / berth', [i.portName, i.berthName].filter(Boolean).join(' / ') || null],
    ['Movement', [i.movementType, i.tradingStatus].filter(Boolean).join(' · ') || null],
    ['Service start', when(i.serviceStart)],
    ['Service end', when(i.serviceEnd)],
    ['Hours', i.hours],
    ['Standby hours', i.standbyHours],
    ['Hourly rate', `${amount(i.hourlyRate, i.currency)} (${i.rateBasis})`],
    ['Special rate reason', i.specialRateReason],
    ['Base charge', amount(i.baseCharge, i.currency)],
    ['Standby fees', amount(i.standbyFees, i.currency)],
    ['Surcharges', amount(i.surchargeAmount, i.currency)],
    [`VAT (${i.vatPercent}%)`, amount(i.vatAmount, i.currency)],
    ['Total', amount(i.netAmount, i.currency)],
    ['Signed off by (tug captain)', i.sofSignedBy],
    ["Confirmed by (ship's master)", i.assistConfirmedBy],
    ['Approved by', i.approvedBy],
  ];
}

export default function SapQueuePage() {
  const [queue, setQueue] = useState([]);
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [pageMsg, setPageMsg] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [inputsFor, setInputsFor] = useState(null); // job whose billing inputs are open

  const load = useCallback(async () => {
    const [q, e] = await Promise.all([api.getSapQueue(), api.getSapErrors()]);
    setQueue(q || []);
    setErrors(e || []);
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setLoadError('');
      try {
        await load();
      } catch (err) {
        setLoadError(err.message || 'Failed to load the SAP queue.');
      } finally {
        setLoading(false);
      }
    })();
  }, [load]);

  const raise = async (row) => {
    setBusyId(row.jobId);
    setPageMsg(null);
    try {
      const res = await api.raiseSap(row.jobId);
      setPageMsg({
        type: 'success',
        text: `Sales Order raised to SAP — ${res.sapSalesOrderId} (${res.transmissionStatus}).`,
      });
      await load();
    } catch (err) {
      setPageMsg({ type: 'danger', text: err.message || 'Failed to raise to SAP.' });
    } finally {
      setBusyId(null);
    }
  };

  const retry = async (row) => {
    setBusyId(row.jobId);
    setPageMsg(null);
    try {
      const res = await api.retrySap(row.jobId);
      setPageMsg({ type: 'success', text: `Retry succeeded — ${res.sapSalesOrderId}.` });
      await load();
    } catch (err) {
      setPageMsg({ type: 'danger', text: err.message || 'Retry failed.' });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="container-fluid py-4" data-testid="sap-page">
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center bg-white p-3 rounded shadow-sm">
            <div>
              <h3 className="text-primary mb-0">
                <i className="fas fa-network-wired me-2" aria-hidden="true" />
                SAP S/4HANA Integration Queue
              </h3>
              <p className="text-muted mb-0 small">
                Raise approved billing records as Sales Orders and resolve sync exceptions.
              </p>
            </div>
            <span className="badge bg-primary p-2">{queue.length} In Queue</span>
          </div>
        </div>
      </div>

      {loadError && (
        <div className="alert alert-danger" data-testid="sap-load-error">
          {loadError}
        </div>
      )}
      {pageMsg && (
        <div className={`alert alert-${pageMsg.type}`} role="alert" data-testid="sap-message">
          {pageMsg.text}
        </div>
      )}

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading…</span>
          </div>
        </div>
      ) : (
        <>
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-header bg-white pt-4 border-bottom-0">
              <h5 className="mb-0 text-secondary">
                <i className="fas fa-list me-2" aria-hidden="true" />
                SAP Integration Billing Queue
              </h5>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="bg-light text-secondary small text-uppercase">
                    <tr>
                      <th>Invoice / Vessel</th>
                      <th>SAP Customer</th>
                      <th>Total</th>
                      <th>Transmission</th>
                      <th>SAP Sales Order</th>
                      <th aria-label="action" />
                    </tr>
                  </thead>
                  <tbody>
                    {queue.length === 0 && (
                      <tr>
                        <td colSpan={6} className="text-center text-muted py-4">
                          No approved bills awaiting SAP posting.
                        </td>
                      </tr>
                    )}
                    {queue.map((r) => (
                      <React.Fragment key={r.jobId}>
                      <tr data-testid="queue-row" data-ref={r.bookingRef}>
                        <td className="fw-bold">
                          {r.bookingRef}
                          {r.invoiceNo && (
                            <span className="d-block text-muted small fw-normal" data-testid="queue-invoice-no">
                              {r.invoiceNo} · {r.lineCount} job line{r.lineCount > 1 ? 's' : ''}
                            </span>
                          )}
                          <span className="d-block text-muted small">{r.vesselName || '—'}</span>
                        </td>
                        <td className="small">{r.sapCustomerId || '—'}</td>
                        <td>{money(r.totalAmount, r.currency)}</td>
                        <td>
                          <span className={`badge ${statusBadge(r.transmissionStatus)}`} data-testid="queue-status">
                            {r.transmissionStatus}
                          </span>
                        </td>
                        <td>
                          {r.sapSalesOrderId ? (
                            <>
                              <code data-testid="sap-order-id">{r.sapSalesOrderId}</code>
                              {r.billingInputs && (
                                <button
                                  type="button"
                                  className="btn btn-link btn-sm d-block p-0"
                                  onClick={() => setInputsFor(inputsFor === r.jobId ? null : r.jobId)}
                                  data-testid="toggle-billing-inputs"
                                >
                                  {inputsFor === r.jobId ? 'Hide' : 'View'} billing inputs
                                </button>
                              )}
                            </>
                          ) : (
                            <span className="text-muted small">—</span>
                          )}
                        </td>
                        <td className="text-end">
                          {r.transmissionStatus !== 'Success' && (
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => raise(r)}
                              disabled={busyId === r.jobId}
                              data-testid="raise-sap"
                            >
                              {busyId === r.jobId && (
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                              )}
                              <i className="fas fa-paper-plane me-1" aria-hidden="true" />
                              Raise to SAP
                            </button>
                          )}
                        </td>
                      </tr>
                      {inputsFor === r.jobId && r.billingInputs && (
                        <tr data-testid="billing-inputs">
                          <td colSpan={6} className="bg-light small">
                            <div className="fw-bold mb-1">Sent to SAP to raise the invoice</div>
                            <div className="row">
                              {inputRows(r.billingInputs).map(([label, value]) => (
                                <div className="col-6 col-md-3 mb-1" key={label}>
                                  <span className="text-muted d-block">{label}</span>
                                  {value ?? '—'}
                                </div>
                              ))}
                            </div>
                            {Array.isArray(r.billingInputs.lines) && (
                              <table className="table table-sm mb-0 mt-2" data-testid="billing-input-lines">
                                <thead>
                                  <tr>
                                    <th>Job</th>
                                    <th>Tug / Ticket</th>
                                    <th>Service time</th>
                                    <th className="text-end">Hours</th>
                                    <th className="text-end">Net</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {r.billingInputs.lines.map((l) => (
                                    <tr key={`${l.jobNo}-${l.ticketNo}`}>
                                      <td>
                                        {l.jobNo || '—'} {l.jobName}
                                      </td>
                                      <td>
                                        {l.tugName || '—'} · {l.ticketNo || '—'}
                                      </td>
                                      <td>
                                        {when(l.serviceStart)} → {when(l.serviceEnd)}
                                      </td>
                                      <td className="text-end">{l.billedHours ?? l.hours}</td>
                                      <td className="text-end">{amount(l.netAmount, r.billingInputs.currency)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="card border-0 shadow-sm" data-testid="error-queue">
            <div className="card-header bg-white pt-4 border-bottom-0">
              <h5 className="mb-0 text-secondary">
                <i className="fas fa-triangle-exclamation me-2 text-danger" aria-hidden="true" />
                Integration Error Queue
              </h5>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-sm align-middle mb-0">
                  <thead className="bg-light text-secondary small text-uppercase">
                    <tr>
                      <th>Draft</th>
                      <th>Transact ID</th>
                      <th>Error Type</th>
                      <th>Raw SAP Error</th>
                      <th aria-label="action" />
                    </tr>
                  </thead>
                  <tbody>
                    {errors.length === 0 && (
                      <tr>
                        <td colSpan={5} className="text-center text-muted py-3">
                          No integration exceptions.
                        </td>
                      </tr>
                    )}
                    {errors.map((e) => (
                      <tr key={e.id} data-testid="error-row" data-ref={e.bookingRef}>
                        <td className="fw-bold">{e.bookingRef}</td>
                        <td>
                          <code>{e.transactId}</code>
                        </td>
                        <td>
                          <span className="badge bg-danger">{e.errorType}</span>
                        </td>
                        <td className="small text-muted">{e.rawError}</td>
                        <td className="text-end">
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => retry(e)}
                            disabled={busyId === e.jobId}
                            data-testid="retry-sap"
                          >
                            <i className="fas fa-rotate-right me-1" aria-hidden="true" />
                            Retry Posting
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
